import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import LowStockBanner from "@/components/bom/LowStockBanner";
import SpareReorderBanner from "@/components/dashboard/SpareReorderBanner";
import VendorOverdueBanner from "@/components/osp/VendorOverdueBanner";

function Banner({ tone, text, action, onAction, testid, actionTestid }) {
  const t = tone === "red"
    ? { box: "border-red-300 bg-red-50", icon: "text-red-600", text: "text-red-900", btn: "bg-red-600 hover:bg-red-700" }
    : tone === "purple"
      ? { box: "border-purple-300 bg-purple-50", icon: "text-purple-600", text: "text-purple-900", btn: "bg-purple-600 hover:bg-purple-700" }
      : { box: "border-amber-300 bg-amber-50", icon: "text-amber-600", text: "text-amber-900", btn: "bg-amber-600 hover:bg-amber-700" };
  return (
    <div className={`rounded-xl border px-4 py-3 flex items-center gap-3 ${t.box}`} data-testid={testid}>
      <AlertTriangle className={`h-5 w-5 shrink-0 ${t.icon}`} />
      <p className={`text-sm font-semibold flex-1 ${t.text}`}>{text}</p>
      <Button size="sm" className={`h-8 text-xs ${t.btn}`} onClick={onAction} data-testid={actionTestid}>{action}</Button>
    </div>
  );
}

// Dashboard-tab alert stack for planners/admins: vendors, stock, purchasing and backup health.
export default function AlertBanners({ canManage, isAdmin, gatePasses, bomItems, poSummary, purchaseSuggestions, lastBackupAt, masterApprovals = { suppliers: 0, customers: 0, bom: 0 }, onTab, onPurchase }) {
  const orderByOverdue = purchaseSuggestions.filter((s) => s.overdue);
  return (
    <>
      <VendorOverdueBanner gatePasses={gatePasses} onReview={canManage ? () => onTab("osp") : undefined} />
      {canManage && <LowStockBanner items={bomItems} onOpenPurchase={onPurchase} />}
      {isAdmin && <SpareReorderBanner items={bomItems} onOpenPurchase={onPurchase} />}
      {canManage && poSummary.overdue_count > 0 && (
        <Banner tone="red" testid="po-overdue-banner" actionTestid="po-overdue-view" action="Review" onAction={() => onTab("purchasing")}
          text={`${poSummary.overdue_count} purchase order(s) overdue — past the supplier lead time.`} />
      )}
      {isAdmin && masterApprovals.suppliers > 0 && (
        <Banner tone="purple" testid="supplier-approval-banner" actionTestid="supplier-approval-review" action="Review" onAction={() => onTab("suppliers")}
          text={`${masterApprovals.suppliers} new supplier record(s) awaiting your approval.`} />
      )}
      {isAdmin && masterApprovals.customers > 0 && (
        <Banner tone="purple" testid="customer-approval-banner" actionTestid="customer-approval-review" action="Review" onAction={() => onTab("customers")}
          text={`${masterApprovals.customers} new customer record(s) awaiting your approval.`} />
      )}
      {isAdmin && masterApprovals.bom > 0 && (
        <Banner tone="purple" testid="bom-approval-banner" actionTestid="bom-approval-review" action="Review" onAction={() => onTab("bom")}
          text={`${masterApprovals.bom} BOM item(s) awaiting your approval — only Active items can be used in recipes and new orders.`} />
      )}
      {canManage && orderByOverdue.length > 0 && (
        <Banner tone="amber" testid="order-by-overdue-banner" actionTestid="order-by-overdue-view" action="Order now" onAction={onPurchase}
          text={<>
            {orderByOverdue.length} material(s) past their order-by date — order now to avoid a stock-out
            <span className="font-normal text-amber-700"> ({orderByOverdue.slice(0, 3).map((s) => s.sku).join(", ")}{orderByOverdue.length > 3 ? "…" : ""})</span>
          </>} />
      )}
      {isAdmin && (
        <div className="mb-4 inline-flex items-center gap-2 text-xs rounded-full border px-3 py-1.5 bg-white shadow-sm" data-testid="dashboard-backup-health">
          <span className={`h-2 w-2 rounded-full ${lastBackupAt ? "bg-emerald-500" : "bg-amber-500"}`} />
          <span className="text-slate-500">Last database backup:</span>
          <span className="font-semibold text-slate-700">{lastBackupAt ? new Date(lastBackupAt).toLocaleString("en-IN") : "none yet"}</span>
        </div>
      )}
    </>
  );
}
